import Loader from 'common/components/Loader';
import React, { RefObject, useCallback, useMemo, useRef } from 'react';
import cn from 'classnames';
import type { BBCommonProps, BBModel } from 'types/story';
import cmsUtils from 'common/utils/cms';
import { COMPONENT_STATES, COMPONENT_TYPE } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import type {
	ITraverseTreeOptions,
	MapChildrenProps,
	ParentTraversalInfo,
	TraverseTreeParams,
	TreeComponent,
	TreeComponentProps,
} from 'client/components/common/BuildingBlocks/utils/traverse-tree-types';
import { useClientSelector } from 'client/reducers';
import { selectStorySymbols } from 'client/reducers/story/selectors';
import { traverseTree } from 'client/components/common/BuildingBlocks/utils/traverse-tree';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { ChildrenWithParentState } from 'client/components/common/BuildingBlocks/ChildrenWithParentState';
import { SliderSlidesContext } from 'client/components/common/BuildingBlocks/Slider/SliderSlides/SliderSlidesContext';
import { processRepeatableComponent } from './process-repeatable-component';
import { useData } from './use-data';
import css from './CmsRepeater.scss';

/**
 * * Behaviour
 *
 * * Editor:
 * - repeater with children rendered as it is... skip all the logic except data fetching
 *
 * * Preview|Published:
 * - repeater rendered without children at traverse tree
 * - repeater fetches data from API
 * - repeater replicates itself n-times
 * - repeater return traverse tree with N-replicated repeaters
 * - this time, traverse tree should render repeater as it is with a children
 * - these repeaters should be marked as "nested" to avoid an infinite replication loop
 *
 * * Components:
 * - <CmsRepeaterEditable />   - render children as it is
 * - <CmsRepeaterReplicator /> - fetch, replicate, render traverse tree with N-replicated repeaters instead of itself
 * - <CmsRepeaterReplica />    - render itself with children as it is,
 * */

type RepeaterReplicatorCommonProps = {
	// The current context of the tree being traversed up to this point
	treeContext: {
		mapChildrenProps: MapChildrenProps;
		options: ITraverseTreeOptions;
		// Raw original Repeater building block model
		rawModel: BBModel;
	};
};

/**
 * The base props for the RepeaterReplicator component before any enhancements.
 * These are the initial properties used by the raw component, prior to being
 * wrapped by HOCs such as `withCardTransitionContext` or others.
 *
 * Use this type when working with the unenhanced version of the component.
 */
type RepeaterReplicatorBaseProps = TreeComponentProps & RepeaterReplicatorCommonProps;

type RepeaterReplicatorProps = BBCommonProps & RepeaterReplicatorCommonProps;

type RepeaterEditableProps = BBCommonProps;

type RepeaterReplicaProps = BBCommonProps;

type Props = RepeaterReplicatorProps | RepeaterEditableProps | RepeaterReplicaProps;

interface EmptyStateProps extends Pick<BBCommonProps, 'setStates'> {
	children: React.ReactNode;
}

const CmsRepeaterEmptyStateView: React.FC<EmptyStateProps> = ({ children, setStates }) => {
	React.useEffect(() => {
		setStates({ [COMPONENT_STATES.EMPTY]: true });
		return () => {
			setStates({ [COMPONENT_STATES.EMPTY]: false });
		};
	}, [setStates]);

	return children;
};

/**
 * Replicates original `CmsRepeater` n-times based on fetched data
 * and renders traverse-tree with N-replicated repeaters
 *
 * Fetch data, replicate, render traverse tree with N-replicated repeaters
 * todo: check, why `on card transition start` animation does not work
 */
const CmsRepeaterReplicator: React.FC<RepeaterReplicatorProps> = props => {
	const { collectionId, items, isLoading } = useData({
		collectionId: props.uiConfig.componentProps.collectionId ?? '',
		pageSize: cmsUtils.getComponentProp(props, UNIFORM_PROPS.collectionPageSize),
		paginationTriggers: cmsUtils.getComponentProp(props, UNIFORM_PROPS.collectionPaginationTriggers),
		collectionFilters: cmsUtils.getComponentProp(props, UNIFORM_PROPS.collectionFilters),
	});

	const symbols = useClientSelector(selectStorySymbols);

	const propsRef = useRef(props);
	propsRef.current = props;

	/**
	 * Retrieves the children from the original repeater component model.
	 * This function accesses the raw model's children through the treeContext
	 * to get the components that need to be replicated.
	 */
	const getChildren = useCallback(() => {
		return (propsRef.current.treeContext.mapChildrenProps ?? {}).children;
	}, []);

	const getParentTraversalInfo: () => ParentTraversalInfo = useCallback(() => {
		const { rawModel, ...treeContext } = propsRef.current.treeContext;
		const parentTreeContext = treeContext.mapChildrenProps ?? {};

		return {
			mapChildrenProps: {
				// drop last index from every parent context field to omit original logical Repeater parent
				path: parentTreeContext.path.split('.').slice(0, -2).join('.'), // "1.children.0{{.children.0}}" 👈
				idPath: parentTreeContext.idPath.split('.').slice(0, -1).join('.'),
				parent: parentTreeContext.parent.slice(0, -1),
				cssParent: parentTreeContext.cssParent,
				isFFC: parentTreeContext.isFFC,
				parentIndexOffset: parseInt(parentTreeContext.path.split('.').at(-1) ?? '', 10) || 0,
			},
			repeaterComponent: {
				originalId: rawModel._id,
			},
		};
	}, []);

	const replicatedTree = useMemo(() => {
		const { rawModel, ...treeContext } = propsRef.current.treeContext;
		if (!collectionId || !items?.total || !treeContext) {
			return null;
		}

		const buildingBlockModel: BBModel = {
			_id: rawModel._id,
			type: rawModel.type,
			uiConfig: rawModel.uiConfig,
			...(rawModel.symbol ? { symbol: rawModel.symbol } : {}),
			children: getChildren(),
		};

		// Create a new component n-times with the same props as the original one
		const { components: replicas } = processRepeatableComponent({
			component: buildingBlockModel,
			items: { [collectionId]: cmsUtils.transformItemsArrayToObject(items.items) },
			symbols,
		});

		return traverseTree({
			tree: replicas,
			options: treeContext.options,
			parentTraversalInfo: getParentTraversalInfo(),
		});
	}, [collectionId, items?.items, items?.total, symbols, getParentTraversalInfo, getChildren]);

	if (!replicatedTree) {
		const tree = getChildren();
		return (
			<RepeaterBody
				uiConfig={props.uiConfig}
				stateAttrs={props.stateAttrs}
				containerRef={props.containerRef}
				parentStates={props.parentStates}
				editableModeProps={props.editableModeProps}
			>
				{isLoading ? (
					<Loader color="black" fullFrame bgColor="rgba(255, 255, 255, 0.18)" />
				) : (
					<CmsRepeaterEmptyStateView setStates={props.setStates}>
						{Array.isArray(tree)
							? traverseTree({
									tree,
									options: propsRef.current.treeContext.options,
									parentTraversalInfo: getParentTraversalInfo(),
								})
							: null}
					</CmsRepeaterEmptyStateView>
				)}
			</RepeaterBody>
		);
	}

	return replicatedTree;
};

type RepeaterBodyProps = Pick<
	RepeaterEditableProps | RepeaterReplicaProps,
	'uiConfig' | 'stateAttrs' | 'containerRef' | 'parentStates' | 'children' | 'editableModeProps' | 'eventListeners'
>;

const RepeaterBody: React.FC<RepeaterBodyProps> = ({ editableModeProps, uiConfig, ...props }) => (
	<div
		{...uiConfig.nodeProps}
		{...props.stateAttrs}
		{...props.eventListeners}
		{...editableModeProps?.nodeProps}
		style={uiConfig.nodeProps?.style}
		className={cn(css.cmsRepeater, uiConfig.nodeProps.className, editableModeProps?.nodeProps?.className)}
		ref={props.containerRef as RefObject<HTMLDivElement>}
	>
		<ChildrenWithParentState states={props.parentStates}>{props.children}</ChildrenWithParentState>
	</div>
);

const CmsRepeaterEditable: React.FC<RepeaterEditableProps> = props => {
	return (
		<RepeaterBody
			uiConfig={props.uiConfig}
			stateAttrs={props.stateAttrs}
			containerRef={props.containerRef}
			parentStates={props.parentStates}
			eventListeners={props.eventListeners}
			editableModeProps={props.editableModeProps}
		>
			{props.children}
		</RepeaterBody>
	);
};

const CmsRepeaterReplica: React.FC<RepeaterReplicaProps> = props => {
	const sliderSlidesContext = React.useContext(SliderSlidesContext);

	const wrapRepeaterReplica = useCallback(
		(content: React.ReactNode, key: string) => {
			if (sliderSlidesContext?.renderSlide) {
				return sliderSlidesContext.renderSlide(content, key);
			}
			return content;
		},
		[sliderSlidesContext]
	);

	return wrapRepeaterReplica(
		<RepeaterBody
			uiConfig={props.uiConfig}
			stateAttrs={props.stateAttrs}
			containerRef={props.containerRef}
			parentStates={props.parentStates}
			editableModeProps={props.editableModeProps}
		>
			{props.children}
		</RepeaterBody>,
		props._id
	);
};

// fixme: noticed that it does not get parent states, but it's children does
// todo: make sure that repeater inside repeater not break the tree. consider to omit it by return null
const CmsRepeater: React.FC<Props> = props => {
	const isHidden = props.uiConfig.componentProps?.styles?.display === 'none';

	if (isHidden) {
		return <RepeaterBody {...props} />;
	}

	if ('isEditableMode' in props && props.isEditableMode) {
		// fetch data, render children as it is
		return <CmsRepeaterEditable {...props} />;
	}

	if ('treeContext' in props) {
		const { treeContext, ..._props } = props;
		// fetch data, replicate, render traverse tree with N-replicated repeaters
		return <CmsRepeaterReplicator {..._props} treeContext={treeContext} />;
	}

	// render itself with children as it is
	return <CmsRepeaterReplica {...props} />;
};

/**
 * Type guard to determine if a component should be typed as a RepeaterReplicatorProps component.
 * This applies to CmsRepeater component that invoke traverse-tree internally.
 *
 * @param props - The component props, including the type.
 * @param isEditor - Whether the editor mode is active.
 * @param parentTraversalInfo - The parent traversal context, if any.
 * @param Component - The React component to check.
 * @returns True if the component should be typed as React.ElementType<RepeaterReplicatorProps>.
 */
function isRepeaterReplicator<ComponentProps>(
	Component: TreeComponent<ComponentProps> | React.ComponentType<RepeaterReplicatorBaseProps>,
	props: TreeComponentProps,
	isEditor: boolean,
	parentTraversalInfo: TraverseTreeParams['parentTraversalInfo']
): Component is React.ComponentType<RepeaterReplicatorBaseProps> {
	return props.type === COMPONENT_TYPE.CMS_REPEATER && !isEditor && !parentTraversalInfo?.repeaterComponent;
}

export default withCardTransitionContext(CmsRepeater);
export { RepeaterReplicatorBaseProps, isRepeaterReplicator, CmsRepeaterReplicator };
